/**
 * The subscriber surface's fixture rows (S1, G-SUB-01/03).
 *
 * The fixture provider is the console's stand-in for a deployment, and this file is
 * that deployment's SUBSCRIBER TABLE — the rows `GET /ops/v1/subscribers` reads and
 * `POST /ops/v1/subscribers` writes into. It is a copy of the SERVER's semantics,
 * not a second policy:
 *
 *  - the identifying fields are stored WHOLE here, and masked on the way OUT, because
 *    that is where the server masks them (`subscriber_view` of
 *    `acs_service/routers/subscribers.py`: the store keeps the real value, because a
 *    masked value cannot be matched against anything);
 *  - the mask is `mask_identifier` of `acs_service/models.py`, ported verbatim: the
 *    LENGTH is kept, a value of two characters or fewer is masked ENTIRELY (every
 *    character becomes `*`), and a longer value has every character but the last two
 *    replaced by `*`; `None` stays
 *    `None` («this subscriber has no contract number» is not a secret);
 *  - a service profile carries NO personal data — a profile is a commercial shape (a
 *    speed, a VLAN, a DBA), not a person — so the profile rows have no mask at all.
 *
 * The rows below are DEMO DATA: the phone numbers and the national ids are the
 * shapes the contract accepts (`^09[0-9]{9}$` and `^[0-9]{10}$`) and nobody's.
 */
import { SUBSCRIBER_IDENTIFYING_FIELDS, type ServiceProfile } from '../api/types';
import { deriveAlarmRows } from './alarms';
import { FIXTURE_DEVICES, type FixtureDevice } from './devices';

/**
 * The three fields that are personal data — `SUBSCRIBER_IDENTIFYING_FIELDS` of
 * `acs_service/models.py`.
 *
 * They are named ONCE in this console, in `api/types.ts` (the console's copy of the
 * server's list), and this file imports that list rather than declaring a second
 * one: a fourth field added to the server's tuple and not to the console's would
 * then be personal data that nobody masks, which is the exact failure the server's
 * own docstring names.
 */
export { SUBSCRIBER_IDENTIFYING_FIELDS };

/**
 * `mask_identifier` of `acs_service/models.py`, ported verbatim.
 *
 * The LENGTH is kept: a masked phone number still tells a reader how long the number
 * is, and a caller that needs the real one has to hold the capability level that shows
 * it. A value of two characters or fewer is masked ENTIRELY (every character becomes
 * `*`), and a longer value has every character but the last two replaced by `*`.
 * `null` stays `null`.
 *
 * The two OVERLOADS are the shape of the server's function and not a convenience:
 * the contract's `mobile` is NOT nullable while `national_id` and `contract_number`
 * are, so a caller holding a plain `string` must not be handed a `string | null`
 * it would then have to guess about. One body serves both — the behaviour is the
 * server's, byte for byte.
 */
export function maskIdentifier(value: string): string;
export function maskIdentifier(value: string | null): string | null;
export function maskIdentifier(value: string | null): string | null {
  if (value === null) return null;
  const text = String(value);
  if (text.length <= 2) return '*'.repeat(text.length);
  return '*'.repeat(text.length - 2) + text.slice(-2);
}

/** One subscriber row as the STORE holds it — the identifying fields are whole here. */
export interface FixtureSubscriber {
  subscriber_id: string;
  mobile: string;
  national_id: string | null;
  contract_number: string | null;
  status: 'active' | 'suspended';
  acs_device_id: string | null;
  service_profile_id: string | null;
  /**
   * The BSS's OWN reference — the same `^sub:` string the frozen v1 `subscriber_ref`
   * names. It is NULLABLE: a subscriber the ACS minted itself has none, and the fixture
   * says so with `null` rather than with a missing key — the server sends the key, and a
   * console that hid it would be a console that claimed a shape the contract does not
   * have.
   */
  bss_subscriber_ref: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * The demo device the FIRST subscriber holds — chosen at module load, not written down.
 *
 * **The 360-degree view must have all six sections, and one of them is the device's
 * ALARMS.** A device that carries no alarm would leave that section empty, and the
 * empty section is indistinguishable from a deployment with no alarms at all — which
 * is the lie this fixture exists not to tell. So the demo subscriber is pointed at a
 * device that (a) is BOUND — it carries a `nas_ref`, so the binding section is a row
 * and not an absence — and (b) carries at least one alarm row.
 *
 * It is SELECTED rather than hard-coded because the fleet is generated: a written-down
 * id would be one more thing to re-point every time the seeds move. The selection
 * reads the same two tables the view reads — `FIXTURE_DEVICES` and the derived alarm
 * rows — so the demo device cannot drift away from the data that backs it.
 */
function demoDeviceWithAlarm(): FixtureDevice {
  const alarmed = new Set(deriveAlarmRows().map((row) => row.acs_device_id));
  const found = FIXTURE_DEVICES.find(
    (device) => alarmed.has(device.acs_device_id) && device.nas_ref !== null,
  );
  if (!found) {
    throw new Error(
      'no fixture device is both bound and carries an alarm: the 360-degree view would ' +
        'have an empty alarm section, which reads as «this deployment has no alarms»',
    );
  }
  return found;
}

/** The device the demo subscriber holds — the one the 360-degree view is demonstrated on. */
export const FIXTURE_SUBSCRIBER_DEVICE: FixtureDevice = demoDeviceWithAlarm();

/**
 * The demo subscribers. The identities follow the server's own shapes — `sub:` plus
 * twelve hex characters, `sprof:` plus eight — because a console that taught the
 * browser a different identity shape would be teaching it a second contract.
 */
export const FIXTURE_SUBSCRIBERS: readonly FixtureSubscriber[] = [
  {
    subscriber_id: 'sub:a1b2c3d4e5f6',
    mobile: '09121234567',
    national_id: '0012345678',
    contract_number: null,
    status: 'active',
    // The device is CHOSEN, not written down: it is a bound device that carries an
    // alarm, so the 360-degree view has all six sections (see `demoDeviceWithAlarm`).
    acs_device_id: FIXTURE_SUBSCRIBER_DEVICE.acs_device_id,
    service_profile_id: 'sprof:0a1b2c3d',
    // The BSS knows this subscriber by its OWN name — the shape every binding the BSS
    // has written carries.
    bss_subscriber_ref: 'sub:SUB-000123',
    created_at: '2026-10-01T08:00:00Z',
    updated_at: '2026-10-01T08:00:00Z',
  },
  {
    subscriber_id: 'sub:b2c3d4e5f6a7',
    mobile: '09355556666',
    national_id: null,
    contract_number: 'C-2026-0001',
    status: 'active',
    acs_device_id: null,
    service_profile_id: 'sprof:1a2b3c4d',
    // A subscriber the ACS minted itself has NO BSS reference — and the row says so
    // with `null`, which is what the server sends.
    bss_subscriber_ref: null,
    created_at: '2026-10-04T11:30:00Z',
    updated_at: '2026-10-04T11:30:00Z',
  },
  {
    subscriber_id: 'sub:c3d4e5f6a7b8',
    mobile: '09000000000',
    national_id: '0022334455',
    contract_number: 'C-2026-0002',
    status: 'suspended',
    acs_device_id: null,
    service_profile_id: null,
    bss_subscriber_ref: null,
    created_at: '2026-10-09T09:15:00Z',
    updated_at: '2026-10-12T14:05:00Z',
  },
];

/** The demo service profiles — the commercial shapes, with no personal data in them. */
export const FIXTURE_SERVICE_PROFILES: readonly ServiceProfile[] = [
  {
    profile_id: 'sprof:0a1b2c3d',
    name: 'SP-INET-1G',
    down_mbps: 1000,
    up_mbps: 100,
    vlan_id: 101,
    dba: { tcont: 'c-inet-1g', assured_mbps: 100, maximum_mbps: 1000 },
    created_at: '2026-10-01T07:45:00Z',
    updated_at: '2026-10-01T07:45:00Z',
  },
  {
    profile_id: 'sprof:1a2b3c4d',
    name: 'SP-INET-256M',
    down_mbps: 256,
    up_mbps: 32,
    vlan_id: 102,
    dba: { tcont: 'c-inet-256m', assured_mbps: null, maximum_mbps: 256 },
    created_at: '2026-10-04T11:20:00Z',
    updated_at: '2026-10-04T11:20:00Z',
  },
];
